import { Button, Label, Modal, Select, Textarea, TextInput } from "flowbite-react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createAgency, updateAgency } from "../../api/agencies";
import LogoUploader from "../uploaders/LogoUploader";
import { getProfiles } from "../../api/profiles";
import { getSubaccounts, getDomains } from "../../api/sendpost";
import ReactSelect from "react-select";
import { getSignatures } from "../../api/signatures";
import { ColorPicker } from "primereact/colorpicker";

const CreateModal = ({ openModal, setOpenModal, fetchAgencies, agencyToEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    address: '',
    country: '',
    legalDisclaimer: '',
    profile: '',
    brandWebsite: '',
    sendpostSubaccount: '',
    sendpostSubaccountId: '',
    sendpostDomain: '',
    sendpostDomainId: '',
    signatureTemplate: '',
    mainColour: '',
  });

  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [sendpostSubaccounts, setSendpostSubaccounts] = useState([]);
  const [sendpostDomains, setSendpostDomains] = useState([]);
  const [signatures, setSignatures] = useState([]);

  const findApiKeyById = useCallback((id) => {
    const subaccount = sendpostSubaccounts.find(account => account.id.toString() === id);

    return subaccount ? subaccount.apiKey : null;
  }, [sendpostSubaccounts])

  useEffect(() => {
    setFormData({
      name: agencyToEdit?.name,
      bio: agencyToEdit?.bio,
      address: agencyToEdit?.address,
      country: agencyToEdit?.country,
      legalDisclaimer: agencyToEdit?.legalDisclaimer,
      profile: agencyToEdit?.profile,
      brandWebsite: agencyToEdit?.brandWebsite,
      sendpostSubaccount: agencyToEdit?.sendpostSubaccount,
      sendpostSubaccountId: agencyToEdit?.sendpostSubaccountId,
      sendpostDomain: agencyToEdit?.sendpostDomain,
      sendpostDomainId: agencyToEdit?.sendpostDomainId,
      signatureTemplate: agencyToEdit?.signatureTemplate,
      mainColour: agencyToEdit?.mainColour,
    });

    if (agencyToEdit?.sendpostDomainId) {
      fetchDomains({
        apiKey: findApiKeyById(agencyToEdit.sendpostSubaccountId),
      });
    }
  }, [agencyToEdit, findApiKeyById]);

  const findSubaccountNameById = (id) => {
    const subaccount = sendpostSubaccounts.find(account => account.id.toString() === id);

    return subaccount ? subaccount.name : null;
  }

  const findDomainNameById = (id) => {
    const domain = sendpostDomains.find(domain => domain.id.toString() === id);

    return domain ? domain.name : null;
  }

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === 'sendpostSubaccount') {
      const apiKey = findApiKeyById(value);

      await fetchDomains({
        apiKey,
      });

      setFormData((prevData) => ({
        ...prevData,
        sendpostSubaccountId: value,
        sendpostSubaccount: findSubaccountNameById(value)
      }));
    } else if (name === 'sendpostDomain') {
      setFormData((prevData) => ({
        ...prevData,
        sendpostDomainId: value,
        sendpostDomain: findDomainNameById(value)
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  }

  const fetchDomains = async (data) => {
    if (!data?.apiKey) {
      return;
    }

    try {
      const domains = await getDomains(data);

      setSendpostDomains(domains);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || 'Failed to fetch domains');
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const data = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'logo') {
          const fileInput = document.querySelector('input[name="logo"]');

          if (fileInput && fileInput.files.length) {
            data.append('logo', fileInput.files[0]);
          }
        } else if (Array.isArray(value)) {
          value.forEach(item => data.append(`${key}[]`, item));
        } else {
          data.append(key, value);
        }
      });

      agencyToEdit ? await updateAgency({
        data,
        id: agencyToEdit._id
      }) : await createAgency(data);

      toast.success(`${formData.name} agency has been ${agencyToEdit ? 'updated' : 'created'} successfully`);
      fetchAgencies();
      setOpenModal(false);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || `Failed to ${agencyToEdit ? 'update' : 'create'} agency`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const _profiles = await getProfiles({
          all: true,
        });

        setProfiles(_profiles.profiles);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || error.message || 'Failed to fetch profiles');
      }
    }

    const fetchSendpostSubaccounts = async () => {
      try {
        const subaccounts = await getSubaccounts();

        setSendpostSubaccounts(subaccounts);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || error.message || 'Failed to fetch subaccounts');
      }
    }

    const fetchSignatures = async () => {
      try {
        const _signatures = await getSignatures();

        setSignatures(_signatures);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || error.message || 'Failed to fetch signatures');
      }
    }

    fetchProfiles();
    fetchSendpostSubaccounts();
    fetchSignatures();
  }, []);

  return (
    <Modal show={openModal} size="3xl" onClose={() => setOpenModal(false)} popup>
      <Modal.Header />
      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-6">
          <h3 className="text-xl font-medium text-gray-900">{agencyToEdit ? 'Edit' : 'Add'} agency</h3>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="bio" value="Bio" />
              </div>
              <Textarea
                id="bio"
                name="bio"
                value={formData.bio || ''}
                onChange={handleChange}
                required
                rows={4}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="logo" value="Logo" />
              </div>
              <LogoUploader
                name="logo"
                value={formData.logo || ''}
                onChange={handleChange}
                existingLogo={agencyToEdit?.logo}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="name" value="Agency Name" />
              </div>
              <TextInput
                id="name"
                name="name"
                value={formData.name || ''}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="mainColour" value="Main Colour" />
              </div>
              <ColorPicker
                value={formData?.mainColour || ''}
                onChange={e => {
                  handleChange({
                    target: {
                      name: 'mainColour',
                      value: e.value,
                    }
                  });
                }}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="address" value="Address" />
              </div>
              <TextInput
                id="address"
                name="address"
                value={formData.address || ''}
                onChange={handleChange}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="country" value="Country" />
              </div>
              <TextInput
                id="country"
                name="country"
                value={formData.country || ''}
                onChange={handleChange}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="legalDisclaimer" value="Legal Disclaimer" />
              </div>
              <TextInput
                id="legalDisclaimer"
                name="legalDisclaimer"
                value={formData.legalDisclaimer || ''}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="managers" value="Managers" />
              </div>
              <ReactSelect
                isMulti
                onChange={value => {
                  handleChange({
                    target: {
                      name: 'managers',
                      value: value.map(v => v.value),
                    },
                  })
                }}
                className="text-xs"
                options={profiles.map(profile => ({
                  value: profile._id,
                  label: profile.name,
                }))}
                defaultValue={agencyToEdit ? profiles.filter(profile => agencyToEdit.managers.includes(profile._id)).map(profile => ({
                  value: profile._id,
                  label: profile.name
                })) : null}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="brandWebsite" value="Brand Website" />
              </div>
              <TextInput
                id="brandWebsite"
                name="brandWebsite"
                type="url"
                value={formData.brandWebsite || ''}
                onChange={handleChange}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="sendpostSubaccount" value="Sendpost Subaccount" />
              </div>
              <Select
                id="sendpostSubaccount"
                required
                onChange={handleChange}
                name="sendpostSubaccount"
                value={formData.sendpostSubaccountId || ''}
              >
                <option
                  value=""
                  disabled
                />
                {sendpostSubaccounts.map(sendpostSubaccount => (
                  <option
                    value={sendpostSubaccount.id}
                    key={sendpostSubaccount.id}
                  >
                    {sendpostSubaccount.name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="sendpostDomain" value="Sendpost Domain" />
              </div>
              <Select
                id="sendpostDomain"
                required
                onChange={handleChange}
                name="sendpostDomain"
                value={formData.sendpostDomainId || ''}
              >
                <option
                  value=""
                  disabled
                />
                {sendpostDomains.map(sendpostDomain => (
                  <option
                    value={sendpostDomain.id}
                    key={sendpostDomain.id}
                  >
                    {sendpostDomain.name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="signatureTemplate" value="Signature Template" />
              </div>
              <Select
                id="signatureTemplate"
                required
                onChange={handleChange}
                name="signatureTemplate"
                value={formData.signatureTemplate || ''}
              >
                <option
                  value=""
                  disabled
                />
                {signatures.map(signature => (
                  <option
                    value={signature}
                    key={signature}
                  >
                    {signature.split('.')[0]}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="w-full">
            <Button
              type="submit"
              isProcessing={loading}
              disabled={loading}
            >
              {agencyToEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default CreateModal;
